export interface AccountSummaryRow {
	CostCenterName: string | null;
	StartNewNum: number | null;
	StartNewCost: number | null;
	StartInUseNum: number | null;
	StartInUseCost: number | null;
	CurrentNewNum: number | null;
	CurrentNewCost: number | null;
	CurrentFirstTurnedNum: number | null;
	CurrentFirstTurnedCost: number | null;
	ValueTurnedOff: number | null;
	CurrentScrappedNum: number | null;
	CurrentScrappedCost: number | null;
	CloseNewNum: number | null;
	CloseNewCost: number | null;
	CloseInUseNum: number | null;
	CloseInUseCost: number | null;
	ActualScrapped: number | null;
	StartTotalNum: number | null;
	StartTotalCost: number | null;
	SubInUseNum: number | null;
	SubInUseCost: number | null;
	StartDateString: string | null;
	CloseDateString: string | null;
	SubTotalNum: number | null;
	SubTotalCost: number | null;
	SubTotalConsumed: number | null;
	TotalNum: number | null;
	TotalCost: number | null;
}

/** The figures the worksheet prints, shared by Summary By Close Date and By Date Range. */
export type AccountSummaryFigures = Omit<
	AccountSummaryRow,
	"CostCenterName" | "StartDateString" | "CloseDateString"
>;

export interface CostCenterOption {
	Id: number;
	Name: string;
	Description: string | null;
}

export interface CloseDateRow {
	CloseDates: string | null;
}

export const EMPTY = "";

const QUANTITY_FORMAT = new Intl.NumberFormat("en-US", {
	maximumFractionDigits: 0,
});

const AMOUNT_FORMAT = new Intl.NumberFormat("en-US", {
	minimumFractionDigits: 2,
	maximumFractionDigits: 2,
});

export const formatQuantity = (value: number | null | undefined): string =>
	value == null ? EMPTY : QUANTITY_FORMAT.format(value);

export const formatAmount = (value: number | null | undefined): string =>
	value == null ? EMPTY : AMOUNT_FORMAT.format(value);

/**
 * The procedure hands the close dates back as MM/DD/YYYY strings with no DISTINCT
 * and no ORDER BY. Sorting them as text would put every December above every
 * January.
 */
export const closeDateSortKey = (value: string): string => {
	const [month, day, year] = value.split("/");
	return year && month && day ? `${year}${month}${day}` : value;
};

export const toCloseDateOptions = (rows: CloseDateRow[]): string[] => {
	const seen = new Set<string>();
	for (const row of rows) {
		const value = row.CloseDates?.trim();
		if (value) seen.add(value);
	}
	return [...seen].sort((a, b) =>
		closeDateSortKey(b).localeCompare(closeDateSortKey(a)),
	);
};

/** The six numeric columns, two per pool of rolls. */
export type WorksheetField =
	| "newQty"
	| "newAmount"
	| "inUseQty"
	| "inUseAmount"
	| "totalQty"
	| "totalAmount";

/**
 * A cell the sheet prints in red: a deduction, which goes inside parentheses, or a
 * figure that came back negative.
 */
export const isRedFigure = (
	row: WorksheetRow | undefined,
	field: WorksheetField,
	value: number | null | undefined,
): boolean => value != null && (value < 0 || !!row?.deductions.includes(field));

/**
 * `line` is an ordinary movement, `subtotal` closes a block and `total` is the
 * figure the next period opens on.
 */
export type WorksheetRowKind = "line" | "subtotal" | "total";

export interface WorksheetRow {
	id: string;
	label: string;
	help: string;
	kind: WorksheetRowKind;
	newQty: number | null;
	newAmount: number | null;
	inUseQty: number | null;
	inUseAmount: number | null;
	totalQty: number | null;
	totalAmount: number | null;
	/** Columns printed inside parentheses, the way the sheet marks a deduction. */
	deductions: readonly WorksheetField[];
}

/**
 * The eight lines of the roll forward, in the order the legacy prints them:
 * beginning inventory plus receipts, the transfer between the two pools, what the
 * period consumed, and the ending inventory the identity has to land on.
 *
 * A null column is a cell the sheet leaves empty, not a zero.
 */
export const buildWorksheetRows = (
	summary: AccountSummaryFigures,
): WorksheetRow[] => [
	{
		id: "beginning-inventory",
		label: "Beginning Inventory",
		help: "What the cost center held when the period opened, carried over from the previous close",
		kind: "line",
		newQty: summary.StartNewNum,
		newAmount: summary.StartNewCost,
		inUseQty: summary.StartInUseNum,
		inUseAmount: summary.StartInUseCost,
		totalQty: summary.StartTotalNum,
		totalAmount: summary.StartTotalCost,
		deductions: [],
	},
	{
		id: "new-rolls-received",
		label: "New Rolls Received",
		help: "Rolls received during the period, at their full purchase cost. A received roll has never been turned, so it lands in New Rolls only",
		kind: "line",
		newQty: summary.CurrentNewNum,
		newAmount: summary.CurrentNewCost,
		inUseQty: null,
		inUseAmount: null,
		totalQty: summary.CurrentNewNum,
		totalAmount: summary.CurrentNewCost,
		deductions: [],
	},
	{
		id: "rolls-first-turned",
		label: "Rolls First Turned",
		help: "Rolls turned for the first time in the period. Not a cost: they leave the new pool at full value and enter the in use pool at their remaining value, so the total does not move",
		kind: "line",
		newQty: summary.CurrentFirstTurnedNum,
		newAmount: summary.CurrentFirstTurnedCost,
		inUseQty: summary.CurrentFirstTurnedNum,
		inUseAmount: summary.CurrentFirstTurnedCost,
		// Hard zeros, not a computed column: the transfer nets out by definition and
		// the legacy prints the zeros to say so.
		totalQty: 0,
		totalAmount: 0,
		deductions: ["newQty", "newAmount"],
	},
	{
		id: "sub-total",
		label: "Sub-Total",
		help: "Everything the cost center was accountable for during the period, before consumption",
		kind: "subtotal",
		newQty: summary.CloseNewNum,
		newAmount: summary.CloseNewCost,
		inUseQty: summary.SubInUseNum,
		inUseAmount: summary.SubInUseCost,
		totalQty: summary.SubTotalNum,
		totalAmount: summary.SubTotalCost,
		deductions: [],
	},
	{
		// The legacy label reads "(#not used in totals)": the quantity, not the amount,
		// is what stays out of the unit totals. Only the missing space is added.
		id: "value-turned-off",
		label: "Value Turned Off (# not used in totals)",
		help: "Dollars of roll material machined away by the period's cuts. It has no quantity and it is not part of the unit totals, but it is part of the cost consumed",
		kind: "line",
		newQty: null,
		newAmount: null,
		inUseQty: null,
		inUseAmount: summary.ValueTurnedOff,
		totalQty: null,
		totalAmount: summary.ValueTurnedOff,
		deductions: ["inUseAmount", "totalAmount"],
	},
	{
		id: "rolls-scrapped",
		label: "Rolls Scrapped or Broken",
		help: "Rolls written off during the period, at their remaining value. A scrapped roll has been turned, so it leaves the in use pool",
		kind: "line",
		newQty: null,
		newAmount: null,
		inUseQty: summary.CurrentScrappedNum,
		inUseAmount: summary.CurrentScrappedCost,
		totalQty: summary.CurrentScrappedNum,
		totalAmount: summary.CurrentScrappedCost,
		deductions: ["inUseQty", "inUseAmount", "totalQty", "totalAmount"],
	},
	{
		id: "sub-total-consumed",
		label: "Sub-Total Consumed",
		help: "The period's total roll expense: metal machined off plus rolls written off",
		kind: "subtotal",
		newQty: null,
		newAmount: null,
		inUseQty: summary.CurrentScrappedNum,
		inUseAmount: summary.SubTotalConsumed,
		totalQty: summary.CurrentScrappedNum,
		totalAmount: summary.SubTotalConsumed,
		deductions: ["inUseQty", "inUseAmount", "totalQty", "totalAmount"],
	},
	{
		id: "ending-inventory",
		label: "Ending Inventory",
		help: "What the cost center holds at the close, and the figure the next period opens on",
		kind: "total",
		newQty: summary.CloseNewNum,
		newAmount: summary.CloseNewCost,
		inUseQty: summary.CloseInUseNum,
		inUseAmount: summary.CloseInUseCost,
		totalQty: summary.TotalNum,
		totalAmount: summary.TotalCost,
		deductions: [],
	},
];

export interface PoolColumn {
	headerName: string;
	help: string;
	quantityField: WorksheetField;
	amountField: WorksheetField;
}

export const POOL_COLUMNS: PoolColumn[] = [
	{
		headerName: "New Rolls",
		help: "Rolls that have never been turned, carried at their purchase cost",
		quantityField: "newQty",
		amountField: "newAmount",
	},
	{
		headerName: "Rolls In Use",
		help: "Rolls turned at least once, carried at their remaining value",
		quantityField: "inUseQty",
		amountField: "inUseAmount",
	},
	{
		headerName: "Total Rolls",
		help: "Both pools together",
		quantityField: "totalQty",
		amountField: "totalAmount",
	},
];

export const HEADER_FIELD_HELP: Record<string, string> = {
	"Start Date:": "First day of the accounting period",
	"Close Date:": "Last day of the accounting period",
	"G/L Account Number:":
		"The cost center this close belongs to, as it is named in the ledger",
};
